<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col sm="12">
        <div class="date-picker">
          <date-range-picker @rangeUpdated="rangeUpdated"></date-range-picker>
        </div>
      </b-col>
    </b-row>
    <b-row>
      <b-col xs="12" lg="4">
        <router-link class="card-link" to="device-usage">
          <b-card no-body>
            <b-card-body class="pb-0">
              <h4 class="mb-0">{{deviceUsageNum}}</h4>
              <p>Times Device Used</p>
            </b-card-body>
          </b-card>
        </router-link>
      </b-col>
      <b-col xs="12" lg="4">
        <router-link class="card-link" to="active-users">
          <b-card no-body>
            <b-card-body class="pb-0">
              <h4 class="mb-0">{{activeUsersNum}}</h4>
              <p>Active Users</p>
            </b-card-body>
          </b-card>
        </router-link>
      </b-col>
      <b-col xs="12" lg="4">
        <b-card no-body>
          <b-card-body class="pb-0">
            <h4 class="mb-0">{{activeDevicesNum}}</h4>
            <p>Active devices</p>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
    <b-card>
      <b-row>
        <b-col sm="12">
          <h4 id="traffic" class="card-title mb-0">Device Usage</h4>
          <div class="small text-muted">This Month</div>
        </b-col>
      </b-row>
      <line-chart-device-usage
        chartId="main-chart-01"
        :deviceUsage="deviceUsage"
        :labels="dateLabels"
        class="chart-wrapper"
        style="height:300px;margin-top:40px;"
        v-bind:height="300"
      ></line-chart-device-usage>
    </b-card>
    <b-row>
      <b-col md="12">
        <b-card header="Usage Data">
          <b-row>
            <b-col md="6" class="my-1">
              <div class="d-flex">
                <div class="pt-1">Per Page:</div>
                <b-form-group class="mb-0 pl-2">
                  <b-form-select :options="pageOptions" v-model="perPageItems" class="mb-1" />
                </b-form-group>
              </div>
            </b-col>
            <b-col md="6" class="my-1">
              <div class="float-right">
                <div class="d-flex">
                  <div class="pt-1">Search:</div>
                  <b-form-group class="mb-0 pl-2">
                    <b-input-group class="mb-1">
                      <b-form-input v-model="filter" placeholder="Type to Search"  />
                    </b-input-group>
                  </b-form-group>
                </div>
              </div>
            </b-col>
          </b-row>
          <b-row>
            <b-col lg="12">
              <b-card>
                <!-- Main table element -->
                <b-table show-empty
                         stacked="md"
                         :items="items"
                         :fields="fields"
                         :current-page="currentPage"
                         :per-page="perPageItems"
                         :filter="filter"
                         :sort-by.sync="sortBy"
                         :sort-desc.sync="sortDesc"
                         :sort-direction="sortDirection"
                         @filtered="onFiltered"
                >
                  <template slot="company_name" slot-scope="row">{{row.value}}</template>
                  <template slot="street_address" slot-scope="row">{{row.value}}</template>
                  <template slot="one_month" slot-scope="row">{{row.value}}</template>
                  <template slot="two_month" slot-scope="row"><span v-bind:class="{
                    green: row.value > 0,
                    red: row.value < 0,
                    blue: row.value == 0 }">{{row.value}}</span></template>
                  <template slot="status" slot-scope="row"><span v-bind:class="{
                    green: row.value === 'Connected',
                    red: row.value === 'Not responding' }">{{row.value}}</span></template>
                </b-table>

                <b-row>
                  <b-col md="12" class="my-1">
                    <div class="float-right">
                      <b-pagination :total-rows="totalRows" :per-page="perPageItems" v-model="currentPage" class="my-0" />
                    </div>
                  </b-col>
                </b-row>

              </b-card>
            </b-col>
          </b-row>

          <b-table show-empty
                   class="mb-0 table-outline"
                   responsive="sm"
                   hover
                   :items="userActivity"
                   :fields="userActivityFields"
                   head-variant="light"
                   :per-page="perPageUsers"
          >
            <div slot="user" slot-scope="item">
              <div>{{item.value.name}}</div>
              <div class="small text-muted">
                <span>
                  <template v-if="item.value.new">New</template>
                  <template v-else>Recurring</template>
                </span> | Registered: {{item.value.registered | moment}}
              </div>
            </div>
            <div slot="activity" slot-scope="item">
              <div class="small text-muted">Last used</div>
              <strong>{{item.value | momentRelative}}</strong>
            </div>
          </b-table>
        </b-card>
      </b-col>
    </b-row>
      <loading :active.sync="isLoading"
      :is-full-page="true"></loading>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import axios from 'axios'
import DateRangePicker from './widgets/DateRangePicker'
import getAdmin from '../mixins/getAdmin'
import getDeviceUsage from '../mixins/getDeviceUsage'
import getRecentUsage from '../mixins/getRecentUsage'
import LineChartDeviceUsage from './charts/LineChartDeviceUsage'
import Loading from 'vue-loading-overlay'
import moment from 'moment'
import onFiltered from './../mixins/onFiltered'
import sortOptions from './../mixins/sortOptions'

export default {
  name: 'dashboard',
  mixins: [
    getAdmin,
    getDeviceUsage,
    getRecentUsage,
    onFiltered,
    sortOptions
  ],
  components: {
    LineChartDeviceUsage,
    DateRangePicker,
    Loading
  },
  data: function () {
    return {
      activeDevicesNum: 0,
      activeUsersNum: 0,
      admin: {},
      items: [],
      fields: [
        { key: 'company_name', label: 'Company name', sortable: true, sortDirection: 'desc' },
        { key: 'street_address', label: 'Location', sortable: true },
        { key: 'one_month', label: 'Monthly usage', sortable: true },
        { key: 'two_month', label: 'Compared to last month', sortable: true },
        { key: 'status', label: 'Status', sortable: true }
      ],
      devices: [],
      deviceUsage: [],
      deviceUsageNum: 0,
      currentPage: 1,
      dateLabels: [],
      filter: null,
      pageOptions: [ 5, 10, 15 ],
      perPageItems: 5,
      perPageUsers: 5,
      sortBy: null,
      sortDesc: false,
      sortDirection: 'asc',
      selected: 'Month',
      totalRows: 0,
      userActivity: [],
      userActivityFields: {
        user: {
          label: 'User'
        },
        activity: {
          label: 'Activity'
        }
      }
    }
  },
  computed: {
    ...mapGetters([
      'isLoading'
    ])
  },
  methods: {
    /**
     * Get admin devices from the backend API to display in a Bootstrap-vue table component.
     */
    getAdminDevices () {
      let self = this

      axios.get(`${process.env.VUE_APP_API_URL}api/devices`, {
        headers: {
          'x-access-token': localStorage.getItem('user-token')
        }
      })
        .then(function (response) {
          let data = response.data.data

          let newData = []

          for (let i = 0; i < data.length; i++) {
            newData.push({
              company_name: data[i].company_name,
              street_address: data[i].street_address,
              one_month: data[i].one_month,
              two_month: data[i].one_month - data[i].two_month,
              status: data[i].status
            })
          }

          self.items = newData
          self.totalRows = newData.length
        })
        .catch(function (error) {
          console.log(error)
        })
    },
    /**
     * Get all admin devices from the backend API to display in a Bootstrap-vue table component.
     */
    getAllAdminDevices () {
      let self = this

      axios.get(`${process.env.VUE_APP_API_URL}api/devices/all`, {
        headers: {
          'x-access-token': localStorage.getItem('user-token')
        }
      })
        .then(function (response) {
          let data = response.data.data

          let newData = []

          for (let i = 0; i < data.length; i++) {
            newData.push({
              company_name: data[i].company_name,
              street_address: data[i].street_address,
              one_month: data[i].one_month,
              two_month: data[i].one_month - data[i].two_month,
              status: data[i].status
            })
          }

          self.items = newData
          self.totalRows = newData.length
        })
        .catch(function (error) {
          console.log(error)
        })
    },
    /**
     * Calls the getDeviceUsage function.
     */
    rangeUpdated () {
      this.getDeviceUsage()
    }
  },
  filters: {
    moment: function (date) {
      return moment(date).format('MMMM Do YYYY')
    },
    momentRelative: function (date) {
      return moment(date).fromNow()
    }
  },
  async mounted () {
    await this.getAdmin()

    if (this.admin.level === 0) {
      this.getAllAdminDevices()
      this.getAllRecentUsage()
      this.getDeviceUsage('DASHBOARD', 'all')
    } else {
      this.getAdminDevices()
      this.getRecentUsage()
      this.getDeviceUsage()
    }
  }
}
</script>

<style>
  /* IE fix */
  #card-chart-01, #card-chart-02 {
    width: 100% !important;
  }
  @keyframes spinAround {
    0% {
        transform: rotate(0deg)
    }
    to {
        transform: rotate(359deg)
    }
}
</style>
